/**
 * Centralized coverage gap calculation utilities
 * Used by both SchedulesPage and Dashboard
 */

import { CoverageRequirement } from "../types/shop.types";
import { WorkingHours } from "../types/schedule.types";
import { startTimeToMinutes, endTimeToMinutes, minutesToTime } from "./timeHelpers";

/**
 * Represents an uncovered time range
 */
export interface UncoveredRange {
  startTime: string;
  endTime: string;
  shortage: number;
  required: number;
}

/**
 * Configuration for coverage calculation
 */
interface CoverageCalcConfig {
  slotDurationMinutes?: number; // Default: 30
}

/**
 * Normalize end time for coverage period calculations
 * Handles overnight periods (e.g., 16:30 to 00:30)
 */
const normalizeEndMinutes = (
  endMinutes: number,
  startMinutes: number
): number => {
  // If end time is before or equal to start time, it means the period spans midnight
  if (endMinutes <= startMinutes) {
    return endMinutes + 24 * 60;
  }
  return endMinutes;
};

/**
 * Check if an employee's working hours cover a specific time slot (in minutes)
 * This works with raw minute values to avoid string conversion issues
 */
const doesWorkingHoursCoverSlotMinutes = (
  whStartMinutes: number,
  whEndMinutes: number,
  slotStartMinutes: number,
  slotEndMinutes: number
): boolean => {
  // Employee covers the slot if they're working during the entire slot
  return whStartMinutes <= slotStartMinutes && whEndMinutes >= slotEndMinutes;
};

/**
 * Calculate coverage at each time slot for a coverage period
 * Returns an array of { slotStart, slotEnd, coverageCount } for each slot
 */
const calculateSlotCoverage = (
  periodStartMinutes: number,
  periodEndMinutes: number,
  employeeWorkingHours: { start: number; end: number }[],
  slotDuration: number
): { slotStart: number; slotEnd: number; coverage: number }[] => {
  const slots: { slotStart: number; slotEnd: number; coverage: number }[] = [];

  for (
    let slotStart = periodStartMinutes;
    slotStart < periodEndMinutes;
    slotStart += slotDuration
  ) {
    const slotEnd = Math.min(slotStart + slotDuration, periodEndMinutes);

    // Count employees covering this slot
    let coverageCount = 0;
    employeeWorkingHours.forEach(({ start, end }) => {
      if (doesWorkingHoursCoverSlotMinutes(start, end, slotStart, slotEnd)) {
        coverageCount++;
      }
    });

    slots.push({ slotStart, slotEnd, coverage: coverageCount });
  }

  return slots;
};

/**
 * Group consecutive uncovered slots into ranges
 */
const groupUncoveredSlots = (
  slots: { slotStart: number; slotEnd: number; coverage: number }[],
  minCoverage: number
): { start: number; end: number; shortage: number }[] => {
  const ranges: { start: number; end: number; shortage: number }[] = [];

  let rangeStart: number | null = null;
  let rangeEnd: number = 0;
  let maxShortage = 0;

  slots.forEach((slot, index) => {
    const shortage = Math.max(0, minCoverage - slot.coverage);

    if (shortage > 0) {
      // Slot is under-covered
      if (rangeStart === null) {
        rangeStart = slot.slotStart;
      }
      rangeEnd = slot.slotEnd;
      maxShortage = Math.max(maxShortage, shortage);
    } else if (rangeStart !== null) {
      // End of an uncovered range
      ranges.push({
        start: rangeStart,
        end: rangeEnd,
        shortage: maxShortage,
      });
      rangeStart = null;
      maxShortage = 0;
    }

    // Handle last slot
    if (index === slots.length - 1 && rangeStart !== null) {
      ranges.push({
        start: rangeStart,
        end: rangeEnd,
        shortage: maxShortage,
      });
    }
  });

  return ranges;
};

/**
 * Calculate uncovered time ranges for a single coverage period
 *
 * @param coveragePeriod - The coverage requirement (startTime, endTime, minCoverage)
 * @param employeeWorkingHours - Array of employee working hours for this shop/date
 * @param config - Optional configuration (slot duration)
 * @returns Array of uncovered time ranges
 */
export const calculateUncoveredRanges = (
  coveragePeriod: CoverageRequirement,
  employeeWorkingHours: WorkingHours[],
  config: CoverageCalcConfig = {}
): UncoveredRange[] => {
  const slotDuration = config.slotDurationMinutes || 30;

  // Convert period times to minutes
  const periodStartMinutes = startTimeToMinutes(coveragePeriod.startTime);
  let periodEndMinutes = endTimeToMinutes(coveragePeriod.endTime);

  // Handle overnight coverage periods
  periodEndMinutes = normalizeEndMinutes(periodEndMinutes, periodStartMinutes);

  // Convert employee working hours to minute ranges
  const empWorkMinutes = employeeWorkingHours.map((wh) => {
    const start = startTimeToMinutes(wh.startTime);
    const end = start + wh.duration * 60;
    return { start, end };
  });

  // Calculate coverage at each slot
  const slots = calculateSlotCoverage(
    periodStartMinutes,
    periodEndMinutes,
    empWorkMinutes,
    slotDuration
  );

  // Group uncovered slots into ranges
  const uncoveredRanges = groupUncoveredSlots(slots, coveragePeriod.minCoverage);

  // Convert back to time strings
  return uncoveredRanges.map((range) => ({
    startTime: minutesToTime(range.start),
    endTime: minutesToTime(range.end),
    shortage: range.shortage,
    required: coveragePeriod.minCoverage,
  }));
};

/**
 * Calculate all coverage gaps for multiple coverage periods
 *
 * @param coveragePeriods - Array of coverage requirements
 * @param employeeWorkingHours - Array of employee working hours for this shop/date
 * @param config - Optional configuration
 * @returns Array of all uncovered time ranges across all periods
 */
export const calculateAllCoverageGaps = (
  coveragePeriods: CoverageRequirement[],
  employeeWorkingHours: WorkingHours[],
  config: CoverageCalcConfig = {}
): UncoveredRange[] => {
  const allGaps: UncoveredRange[] = [];

  coveragePeriods.forEach((period) => {
    const gaps = calculateUncoveredRanges(period, employeeWorkingHours, config);
    allGaps.push(...gaps);
  });

  return allGaps;
};

/**
 * Check if a time slot (in minutes) is covered by the given working hours
 * Used for per-slot coverage counting in dashboards
 *
 * @param slotMinutes - The slot time in minutes from midnight (can be > 1440 for overnight)
 * @param employeeWorkingHours - Array of employee working hours
 * @returns Number of employees covering this slot
 */
export const countEmployeesCoveringSlot = (
  slotMinutes: number,
  employeeWorkingHours: WorkingHours[]
): number => {
  const slotEnd = slotMinutes + 30; // 30-minute slots

  let count = 0;
  employeeWorkingHours.forEach((wh) => {
    const whStart = startTimeToMinutes(wh.startTime);
    const whEnd = whStart + wh.duration * 60;

    if (doesWorkingHoursCoverSlotMinutes(whStart, whEnd, slotMinutes, slotEnd)) {
      count++;
    }
  });

  return count;
};

/**
 * Generate time slots for a coverage period as minute values
 * This avoids the issue of losing overnight context when converting to/from strings
 *
 * @param startTime - Period start time (HH:mm)
 * @param endTime - Period end time (HH:mm)
 * @param slotDuration - Slot duration in minutes (default: 30)
 * @returns Array of slot start times in minutes
 */
export const generateSlotMinutes = (
  startTime: string,
  endTime: string,
  slotDuration: number = 30
): number[] => {
  const startMinutes = startTimeToMinutes(startTime);
  let endMinutes = endTimeToMinutes(endTime);

  // Handle overnight periods
  endMinutes = normalizeEndMinutes(endMinutes, startMinutes);

  const slots: number[] = [];
  for (let m = startMinutes; m < endMinutes; m += slotDuration) {
    slots.push(m);
  }

  return slots;
};

/**
 * Check if coverage requirements are fully met for a period
 *
 * @param coveragePeriod - The coverage requirement
 * @param employeeWorkingHours - Array of employee working hours
 * @returns true if all slots are adequately covered
 */
export const isCoverageMet = (
  coveragePeriod: CoverageRequirement,
  employeeWorkingHours: WorkingHours[]
): boolean => {
  const gaps = calculateUncoveredRanges(coveragePeriod, employeeWorkingHours);
  return gaps.length === 0;
};
